// src/pages/project/ProjectListPage.tsx

import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useProject, useI18n } from '@react/hooks';
import { Button, Input, Badge, ErrorAlert } from '@react/components';
import {
  filterProjects,
  sortProjectsByActivity,
  projectFilters,
  projectFilterToStatus,
  projectStatusMap,
  methodologyMap,
  getStatusMapping,
  getInitials,
  getAvatarColor,
  formatRelativeTime,
  formatProgress,
  getEntityId,
  type ProjectFilter,
} from '@/features/project/logic';

export const ProjectListPage = observer(() => {
  const store = useProject();
  const { t, locale } = useI18n();

  const [projects, setProjects] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  // 'all' by default: new projects are created as Planning, and an 'active'
  // default silently hid them right after creation.
  const [statusFilter, setStatusFilter] = useState<ProjectFilter>('all');

  const fetchProjects = (filter: ProjectFilter, searchTerm: string) => {
    setIsLoading(true);
    setError(null);

    const filters: Record<string, any> = {};
    // The BFF parses status by enum name (ParseProjectStatus).
    const mappedStatus = projectFilterToStatus[filter];
    if (mappedStatus) {
      filters.status = mappedStatus;
    }
    if (searchTerm) {
      filters.search = searchTerm;
    }

    store.ListProjects({
      pagination: { cursor: '', limit: 100, direction: 'forward' as const },
      sort: [],
      view: 0,
      date_range: {} as any,
      filters: filters as any,
    })
      .then(() => {
        setProjects(sortProjectsByActivity(store.items));
      })
      .catch((err: any) => setError(err instanceof Error ? err.message : t('project.loadError')))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    fetchProjects(statusFilter, search);
  }, [statusFilter]);

  useEffect(() => {
    if (!isLoading) {
      setProjects(sortProjectsByActivity(store.items));
    }
  }, [store.items]);

  const filtered = useMemo(
    () => filterProjects(projects, search, statusFilter),
    [projects, search, statusFilter],
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('project.list')}</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{t('project.listDescription')}</p>
        </div>
        <Link to="/projects/new">
          <Button size="sm">{t('project.create')}</Button>
        </Link>
      </div>

      {/* Search */}
      <Input
        placeholder={t('project.searchPlaceholder')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="w-full"
      />

      {/* Status filter tabs */}
      <div className="flex gap-1 border-b border-border overflow-x-auto">
        {projectFilters.map(f => (
          <button
            key={f.key}
            onClick={() => setStatusFilter(f.key)}
            className={`px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${
              statusFilter === f.key
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t(f.labelKey)}
          </button>
        ))}
      </div>

      {/* Error */}
      {(error != null || store.error != null) && (
        <ErrorAlert error={store.error ?? error} dismissible onDismiss={() => { setError(null); store.clearError(); }} />
      )}

      {/* Loading */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="rounded-lg border border-border p-4 animate-pulse">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 rounded-lg bg-muted flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-2/3 rounded bg-muted" />
                  <div className="h-3 w-1/3 rounded bg-muted" />
                </div>
              </div>
              <div className="h-3 w-full rounded bg-muted mb-2" />
              <div className="h-3 w-4/5 rounded bg-muted mb-4" />
              <div className="h-2 w-full rounded-full bg-muted" />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        /* Empty state */
        <div className="py-16 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
            <svg className="h-8 w-8 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
            </svg>
          </div>
          <p className="text-lg font-medium text-foreground">
            {search ? t('project.noSearchResults') : t('project.noItems')}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {search ? t('project.tryDifferentSearch') : t('project.noItemsDescription')}
          </p>
          {!search && (
            <Link to="/projects/new" className="mt-4 inline-block">
              <Button size="sm">{t('project.create')}</Button>
            </Link>
          )}
        </div>
      ) : (
        /* Project cards grid */
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(project => (
            <ProjectCard key={getEntityId(project)} project={project} locale={locale} />
          ))}
        </div>
      )}
    </div>
  );
});

function ProjectCard({ project, locale }: { project: any; locale: string }) {
  const { t } = useI18n();
  const id = getEntityId(project);
  const status = getStatusMapping(projectStatusMap, project.status);
  const methodology = project.methodology ? methodologyMap[project.methodology] : null;
  const progress = formatProgress(project.completed_count ?? 0, project.item_count ?? 0);
  const memberCount = project.member_count ?? 0;

  return (
    <Link
      to={`/projects/${id}`}
      className="block rounded-lg border border-border p-4 transition-colors hover:bg-muted/50 hover:border-primary/30 group"
    >
      {/* Header: icon + name + status */}
      <div className="flex items-start gap-3 mb-3">
        <div className="flex-shrink-0">
          {project.icon ? (
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted text-lg">
              {project.icon}
            </div>
          ) : (
            <div
              className="flex h-10 w-10 items-center justify-center rounded-lg text-sm font-semibold text-white"
              style={{ backgroundColor: project.color || getAvatarColor(project.name || '') }}
            >
              {getInitials(project.name || '')}
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-semibold text-foreground group-hover:text-primary transition-colors">
              {project.name || t('common.untitled')}
            </span>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            {project.key && (
              <span className="text-xs font-mono text-muted-foreground">{project.key}</span>
            )}
            <Badge variant={status.variant as any} size="sm">
              {t(status.labelKey)}
            </Badge>
          </div>
        </div>
      </div>

      {/* Description */}
      {project.description && (
        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
          {project.description}
        </p>
      )}

      {/* Progress bar */}
      {(project.item_count ?? 0) > 0 && (
        <div className="mb-3">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
            <span>{t('project.progress')}</span>
            <span>{progress}%</span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full bg-primary transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Footer: methodology, members, time */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <div className="flex items-center gap-3">
          {methodology && (
            <span>{t(methodology.labelKey)}</span>
          )}
          {memberCount > 0 && (
            <span className="flex items-center gap-0.5">
              <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0Zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0Z" />
              </svg>
              {memberCount}
            </span>
          )}
        </div>
        <span className="flex-shrink-0">
          {formatRelativeTime(project.updated_at ?? project.created_at, locale)}
        </span>
      </div>
    </Link>
  );
}

export default ProjectListPage;
